import { formatEventWhen, type EventWhenInput } from '@utils/datetime';

export type EventWhenVariant = 'date-time' | 'time' | 'date';

/** U+00A0: holds the bracket's words together on one line. */
const NO_BREAK_SPACE = '\u00A0';

interface EventWhenProps {
  event: EventWhenInput;
  variant: EventWhenVariant;
  /**
   * `'event'` (default) — the event PAGE's frame: in-person events render in the
   * venue's zone with the viewer's start time bracketed. `'viewer'` — the LIST
   * frame: everything renders in the viewer's own zone, no bracket. See
   * `formatEventWhen` in `@utils/datetime` and docs/date-formatting/design.md.
   */
  frame?: 'event' | 'viewer';
  /** Classes for the outer span — callers pass their row's truncate/typography. */
  className?: string;
}

/**
 * An event's date and/or time, framed per `frame` (default `'event'`): the
 * event's own zone with the viewer's start time in brackets for in-person
 * events elsewhere — "Tue 16 Jun, 19:00 - 21:00 (17:00 your time)" — or, with
 * `frame="viewer"`, the viewer's own zone with no bracket. Plain text inside
 * one span so the caller's `truncate` clips the whole line; the bracket is
 * muted so the event's local time stays dominant. Its words are joined by
 * no-break spaces, so where a caller lets the line wrap (the PIN card) the
 * bracket moves to the next line whole, never "(17:00 Wed your" / "time)";
 * `wrap-anywhere` lets it break inside only when it cannot fit a line on its
 * own (very large text), so it is never clipped. Truncating callers are
 * nowrap, so nothing changes there.
 */
export function EventWhen({ event, variant, frame = 'event', className }: EventWhenProps) {
  const { date, time, viewer } = formatEventWhen(event, undefined, frame);
  const main =
    variant === 'date' ? date : variant === 'time' ? time : [date, time].filter(Boolean).join(', ');
  if (!main) return null;

  const bracket =
    variant !== 'date' && viewer
      ? `(${viewer.day ? `${viewer.time} ${viewer.day}` : viewer.time} your time)`.replace(
          / /g,
          NO_BREAK_SPACE,
        )
      : null;

  return (
    <span className={className}>
      {main}
      {bracket && (
        <>
          {' '}
          <span className="text-mb-grey-300 wrap-anywhere">{bracket}</span>
        </>
      )}
    </span>
  );
}
